import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import MapComponent from "./../maps/MapComponent";
import { authStorage } from "../../utils/storage";

const CT_DEFAULT_COORDINATES = [41.6032, -73.0877];

function Town() {
  const [townList, setTownList] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [townCoordinates, setTownCoordinates] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [townFound, setTownFound] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const { town } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const token = authStorage.getToken();
    if (!token) { navigate("/login"); return; }

    setIsMapLoading(true);
    setDataLoaded(false);
    setTownFound(true);

    fetch(`/api/town/${town}`, {
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
    })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => {
        setTownList(data);
        setTownFound(data && data.length > 0);
        setDataLoaded(true);
        const ids = (data || []).map(u => u.id).filter(Boolean);
        if (ids.length) {
          fetch(`/api/unit-metrics?ids=${ids.join(',')}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
            .then(r => (r.ok ? r.json() : {}))
            .then(setMetrics)
            .catch(() => setMetrics({}));
        }
      })
      .catch(() => { setTownFound(false); setDataLoaded(true); });

    fetch('/CT_Municipalities.geojson')
      .then(r => r.json())
      .then(data => {
        try {
          const feature = data.features.find(
            f => f.properties.Municipality.toLowerCase() === town.toLowerCase()
          );
          if (feature?.geometry) {
            const pts = extractAllPoints(feature.geometry.coordinates);
            const center = pts.length > 0 ? calculateAveragePoint(pts) : null;
            setTownCoordinates(isValidCoordinate(center) ? center : CT_DEFAULT_COORDINATES);
          } else {
            setTownCoordinates(CT_DEFAULT_COORDINATES);
          }
        } catch {
          setTownCoordinates(CT_DEFAULT_COORDINATES);
        } finally {
          setIsMapLoading(false);
        }
      })
      .catch(() => { setTownCoordinates(CT_DEFAULT_COORDINATES); setIsMapLoading(false); });
  }, [town, navigate]);

  const handleBackClick = () => {
    const fromLetter = location.state?.fromLetter || town.charAt(0).toUpperCase();
    navigate('/towns', { state: { scrollToLetter: fromLetter } });
  };

  const extractAllPoints = (coordinates) => {
    const points = [];
    const extract = (coord) => {
      if (!Array.isArray(coord)) return;
      if (coord.length === 2 && typeof coord[0] === 'number' && typeof coord[1] === 'number') {
        points.push(coord);
      } else {
        coord.forEach(c => extract(c));
      }
    };
    extract(coordinates);
    return points;
  };

  const calculateAveragePoint = (points) => {
    if (!points.length) return null;
    let sumLat = 0, sumLon = 0, valid = 0;
    points.forEach(([lon, lat]) => {
      if (!isNaN(lon) && !isNaN(lat)) { sumLon += lon; sumLat += lat; valid++; }
    });
    return valid > 0 ? [sumLat / valid, sumLon / valid] : null;
  };

  const isValidCoordinate = (coord) =>
    coord && Array.isArray(coord) && coord.length === 2 &&
    !isNaN(coord[0]) && !isNaN(coord[1]) &&
    Math.abs(coord[0]) <= 90 && Math.abs(coord[1]) <= 180;

  const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
  const fmtN = (v) => (v === null ? '—' : v.toLocaleString());
  // Aggregate the town's book: active jobs, dues payers, members, % paying
  let tJobs = 0, hasJobs = false, tDues = 0, hasDues = false, tMembers = 0, hasMembers = false;
  townList.forEach((u) => {
    const m = metrics[u.id] || {};
    const aj = num(m.ActiveJobCount), dp = num(m.DuesPayers), tm = num(m.TotalMembers);
    if (aj !== null) { tJobs += aj; hasJobs = true; }
    if (dp !== null) { tDues += dp; hasDues = true; }
    if (tm !== null) { tMembers += tm; hasMembers = true; }
  });
  const pctPaying = hasDues && hasMembers && tMembers > 0 ? `${((tDues / tMembers) * 100).toFixed(1)}%` : null;
  const TownStat = ({ label, value }) => (
    <div style={{ minWidth: 84 }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: 'white', fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>{value}</div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.75)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginTop: 3 }}>{label}</div>
    </div>
  );

  return (
    <>
      {/* Map */}
      <div className="town-map" style={{ overflow: 'hidden', borderRadius: 'var(--radius-lg)', marginBottom: 20 }}>
        {isMapLoading || !townCoordinates ? (
          <div className="town-map-loading" style={{ height: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-sunken)', borderRadius: 'var(--radius-lg)' }}>
            <span style={{ color: 'var(--fg-3)', fontSize: 13.5 }}>Loading map…</span>
          </div>
        ) : (
          <MapComponent
            center={townCoordinates}
            zoom={townCoordinates[0] === CT_DEFAULT_COORDINATES[0] ? 8 : 12}
          />
        )}
      </div>

      {/* Back + heading */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 24 }}>
        <button onClick={handleBackClick} className="btn btn-primary">
          ← Back to Towns
        </button>
      </div>

      {/* Town hero — the town's book of business at a glance */}
      <div style={{
        background: 'linear-gradient(135deg, var(--accent) 0%, #0093CB 100%)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px 28px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        gap: 24,
        flexWrap: 'wrap',
      }}>
        <div style={{ minWidth: 200 }}>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'white', letterSpacing: '-0.01em' }}>{town}</div>
          <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)', marginTop: 3, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em' }}>
            {dataLoaded && townFound ? `${townList.length} bargaining unit${townList.length !== 1 ? 's' : ''}` : 'Town'}
          </div>
        </div>
        {dataLoaded && townFound && (
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap', alignItems: 'center', marginLeft: 'auto', paddingLeft: 8 }}>
            <TownStat label="Units" value={townList.length} />
            <TownStat label="Active Jobs" value={hasJobs ? fmtN(tJobs) : '—'} />
            <TownStat label="Dues Payers" value={hasDues ? fmtN(tDues) : '—'} />
            <TownStat label="Members" value={hasMembers ? fmtN(tMembers) : '—'} />
            {pctPaying && <TownStat label="% Paying" value={pctPaying} />}
          </div>
        )}
      </div>

      {/* Units */}
      {!dataLoaded ? (
        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--fg-3)', fontSize: 13.5 }}>Loading…</div>
      ) : !townFound || townList.length === 0 ? (
        <div className="card card-pad" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 14, color: 'var(--fg-2)' }}>No bargaining units found in {town}.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          {townList.map((item, i) => (
            <div
              key={i}
              onClick={() => navigate(`/unit-info/${item.id}?town=${encodeURIComponent(town)}`)}
              style={{
                padding: '14px 20px',
                borderBottom: i < townList.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                cursor: 'pointer',
                transition: 'background 150ms',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-sunken)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ marginBottom: 5 }}>
                    <span style={{
                      display: 'inline-flex',
                      padding: '3px 10px',
                      borderRadius: 999,
                      background: 'var(--bg-accent-soft)',
                      color: 'var(--accent)',
                      fontSize: 12,
                      fontWeight: 600,
                      fontFamily: 'var(--font-mono)',
                      letterSpacing: '0.02em',
                    }}>
                      {item.LOCAL} - {String(item.UNIT || 0).padStart(4, '0')}
                    </span>
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 14.5, color: 'var(--fg-1)' }}>{item.NAME}</div>
                </div>
                <div style={{ display: 'flex', gap: 22, alignItems: 'center' }}>
                  {[['Active Jobs', num((metrics[item.id] || {}).ActiveJobCount)],
                    ['Dues Payers', num((metrics[item.id] || {}).DuesPayers)]].map(([label, v]) => (
                    <div key={label} style={{ textAlign: 'right', minWidth: 74 }}>
                      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, fontWeight: 600, color: 'var(--fg-1)', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtN(v)}
                      </div>
                      <div style={{ fontSize: 10.5, color: 'var(--fg-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

export default Town;
